import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/base/PageTransition';
import SEOHead from '../../components/base/SEOHead';

type SectionId = 'overview' | 'manual' | 'source' | 'other' | 'maintain';

interface NavItem {
  id: SectionId;
  label: string;
  icon: string;
  group: string;
}

interface TocItem {
  id: string;
  label: string;
  level: number;
}

const navItems: NavItem[] = [
  { id: 'overview', label: '安装概览', icon: 'ri-home-4-line', group: '安装' },
  { id: 'manual', label: '全局安装（手动）', icon: 'ri-terminal-line', group: '安装' },
  { id: 'source', label: '从源代码', icon: 'ri-code-s-slash-line', group: '安装' },
  { id: 'other', label: '其他安装选项', icon: 'ri-apps-line', group: '安装' },
  { id: 'maintain', label: '维护', icon: 'ri-tools-line', group: '维护' },
];

const tocMap: Record<SectionId, TocItem[]> = {
  overview: [
    { id: 'ov-quick', label: '快速安装', level: 1 },
    { id: 'ov-requirements', label: '系统要求', level: 1 },
    { id: 'ov-installer', label: '安装器脚本', level: 1 },
    { id: 'ov-after', label: '安装后', level: 1 },
    { id: 'ov-path', label: '故障排除：PATH', level: 1 },
  ],
  manual: [
    { id: 'man-npm', label: '使用 npm', level: 1 },
    { id: 'man-pnpm', label: '使用 pnpm', level: 1 },
    { id: 'man-methods', label: 'npm vs git', level: 1 },
    { id: 'man-flags', label: '常用标志', level: 1 },
    { id: 'man-env', label: '环境变量', level: 1 },
  ],
  source: [
    { id: 'src-steps', label: '构建步骤', level: 1 },
    { id: 'src-tips', label: '注意事项', level: 1 },
  ],
  other: [
    { id: 'oth-docker', label: 'Docker', level: 1 },
    { id: 'oth-nix', label: 'Nix', level: 1 },
    { id: 'oth-ansible', label: 'Ansible', level: 1 },
    { id: 'oth-bun', label: 'Bun（实验性）', level: 1 },
    { id: 'oth-hosting', label: '托管与部署', level: 1 },
  ],
  maintain: [
    { id: 'mnt-update', label: '更新', level: 1 },
    { id: 'mnt-migrate', label: '迁移指南', level: 1 },
    { id: 'mnt-uninstall', label: '卸载', level: 1 },
  ],
};

const groups = ['安装', '维护'];

// ─── Shared Components ────────────────────────────────────────────────────────

function CodeBlock({ code, language = 'shell' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-5">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-xl px-5 py-2.5 border border-slate-700 border-b-0">
        <span className="text-sm text-slate-400 font-mono">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-xl p-5 overflow-x-auto">
        <code className="text-base text-slate-300 font-mono leading-loose">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400', title: '说明' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400', title: '注意' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400', title: '提示' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-xl p-5 my-5`}>
      <div className="flex items-start gap-3">
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-xl`}></i>
        </div>
        <div className="text-base text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-2xl font-bold text-white mt-12 mb-5 flex items-center gap-2.5 scroll-mt-24">
      <span className="w-1.5 h-6 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg font-semibold text-slate-200 mt-8 mb-4">{children}</h3>
  );
}

// ─── Section Contents ─────────────────────────────────────────────────────────

function OverviewContent() {
  const [copiedQuick, setCopiedQuick] = useState(false);
  const handleCopyQuick = () => {
    navigator.clipboard.writeText('curl -fsSL https://openclaw.ai/install.sh | bash');
    setCopiedQuick(true);
    setTimeout(() => setCopiedQuick(false), 2000);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">安装</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          除非有特殊原因，否则请使用安装器。它会设置 CLI 并运行新手引导。
        </p>
      </div>

      {/* Quick Install Banner */}
      <div id="ov-quick" className="scroll-mt-24 flex flex-col md:flex-row items-start md:items-center gap-5 bg-gradient-to-r from-cyan-500/10 to-slate-800 border border-cyan-500/20 rounded-2xl p-6 mb-10">
        <div className="w-14 h-14 flex items-center justify-center bg-cyan-400/10 rounded-xl flex-shrink-0">
          <i className="ri-rocket-2-line text-3xl text-cyan-400"></i>
        </div>
        <div className="flex-1">
          <div className="text-white font-semibold text-base mb-2">⚡ 快速安装（推荐）</div>
          <div className="font-mono text-base text-cyan-300 bg-slate-900 rounded-lg px-5 py-3 border border-slate-700 inline-block">
            curl -fsSL https://openclaw.ai/install.sh | bash
          </div>
        </div>
        <button
          onClick={handleCopyQuick}
          className="flex items-center gap-2 px-5 py-3 bg-cyan-400 hover:bg-cyan-300 text-slate-900 rounded-lg text-base font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className={copiedQuick ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copiedQuick ? '已复制' : '复制命令'}
        </button>
      </div>

      <SectionTitle id="ov-requirements">系统要求</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {[
          { icon: 'ri-nodejs-line', title: 'Node.js', desc: '版本 ≥ 22', color: 'text-green-400' },
          { icon: 'ri-apple-line', title: '操作系统', desc: 'macOS、Linux 或 Windows（WSL2）', color: 'text-slate-300' },
          { icon: 'ri-tools-line', title: 'pnpm', desc: '仅从源代码构建时需要', color: 'text-yellow-400' },
        ].map((item, i) => (
          <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
            <div className={`text-3xl mb-3 ${item.color}`}><i className={item.icon}></i></div>
            <div className="text-white font-semibold text-base mb-2">{item.title}</div>
            <div className="text-slate-400 text-base">{item.desc}</div>
          </div>
        ))}
      </div>

      <SectionTitle id="ov-installer">安装器脚本（推荐）</SectionTitle>
      <p className="text-slate-300 text-base leading-relaxed mb-4">
        通过 npm 全局安装 <code className="text-cyan-300 bg-slate-800 px-2 py-1 rounded text-sm">openclaw</code> 并运行新手引导。
      </p>
      <CodeBlock code="curl -fsSL https://openclaw.ai/install.sh | bash" />
      <p className="text-slate-400 text-base mb-3">Windows（PowerShell）：</p>
      <CodeBlock code="iwr -useb https://openclaw.ai/install.ps1 | iex" language="powershell" />
      <p className="text-slate-400 text-base mb-3">下一步（如果你跳过了新手引导）：</p>
      <CodeBlock code="openclaw onboard --install-daemon" />
      <p className="text-slate-400 text-base mb-3">安装器标志：</p>
      <CodeBlock code="curl -fsSL https://openclaw.ai/install.sh | bash -s -- --help" />
      <p className="text-slate-400 text-base mb-3">非交互式（跳过新手引导）：</p>
      <CodeBlock code="curl -fsSL https://openclaw.ai/install.sh | bash -s -- --no-onboard" />

      <SectionTitle id="ov-after">安装后</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        {[
          { cmd: 'openclaw onboard --install-daemon', desc: '运行新手引导', icon: 'ri-user-star-line' },
          { cmd: 'openclaw doctor', desc: '快速检查', icon: 'ri-stethoscope-line' },
          { cmd: 'openclaw status', desc: '检查 Gateway 网关健康状态', icon: 'ri-heart-pulse-line' },
          { cmd: 'openclaw dashboard', desc: '打开仪表板', icon: 'ri-dashboard-line' },
        ].map((item, i) => (
          <div key={i} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-2.5 mb-4">
              <i className={`${item.icon} text-cyan-400 text-lg`}></i>
              <span className="text-white font-medium text-base">{item.desc}</span>
            </div>
            <code className="text-cyan-300 text-sm font-mono">{item.cmd}</code>
          </div>
        ))}
      </div>

      <SectionTitle id="ov-path">故障排除：找不到 openclaw（PATH）</SectionTitle>
      <p className="text-slate-400 text-base mb-3">快速诊断：</p>
      <CodeBlock code={`node -v\nnpm -v\nnpm prefix -g\necho "$PATH"`} />
      <InfoBox type="warning">
        如果 <code className="text-amber-300 bg-slate-800 px-1.5 rounded text-sm">$(npm prefix -g)/bin</code>（macOS/Linux）不在 <code className="text-amber-300 bg-slate-800 px-1.5 rounded text-sm">echo "$PATH"</code> 的输出中，你的 shell 无法找到全局 npm 二进制文件（包括 <code className="text-amber-300 bg-slate-800 px-1.5 rounded text-sm">openclaw</code>）。
      </InfoBox>
      <p className="text-slate-400 text-base mb-3">修复：将其添加到你的 shell 启动文件（zsh：<code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">~/.zshrc</code>，bash：<code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">~/.bashrc</code>）：</p>
      <CodeBlock code={`# macOS / Linux\nexport PATH="$(npm prefix -g)/bin:$PATH"`} />
      <p className="text-slate-400 text-base">在 Windows 上，将 <code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">npm prefix -g</code> 的输出添加到你的 PATH。然后打开新终端（或在 zsh 中执行 <code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">rehash</code> / 在 bash 中执行 <code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">hash -r</code>）。</p>

      <div className="mt-12 pt-8 border-t border-slate-800">
        <p className="text-slate-400 text-base mb-5">安装完成后，继续配置：</p>
        <div className="flex flex-wrap gap-4">
          <Link to="/quick-start" className="flex items-center gap-2 px-5 py-3 bg-cyan-400 text-slate-900 rounded-lg text-base font-semibold hover:bg-cyan-300 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-rocket-line"></i>快速开始
          </Link>
          <Link to="/channels" className="flex items-center gap-2 px-5 py-3 bg-slate-700 text-white rounded-lg text-base font-semibold hover:bg-slate-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-message-3-line"></i>配置消息渠道
          </Link>
          <Link to="/models" className="flex items-center gap-2 px-5 py-3 bg-slate-700 text-white rounded-lg text-base font-semibold hover:bg-slate-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-brain-line"></i>接入 AI 模型
          </Link>
        </div>
      </div>
    </div>
  );
}

function ManualContent() {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">全局安装（手动）</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          如果你已经有 Node，可以直接通过 npm 或 pnpm 手动安装。
        </p>
      </div>

      <SectionTitle id="man-npm">使用 npm</SectionTitle>
      <CodeBlock code="npm install -g openclaw@latest" />
      <InfoBox type="tip">
        如果你全局安装了 libvips（macOS 上通过 Homebrew 安装很常见）且 <code className="text-emerald-300 bg-slate-800 px-1.5 rounded text-sm">sharp</code> 安装失败，请强制使用预构建二进制文件：
      </InfoBox>
      <CodeBlock code="SHARP_IGNORE_GLOBAL_LIBVIPS=1 npm install -g openclaw@latest" />

      <SectionTitle id="man-pnpm">使用 pnpm</SectionTitle>
      <CodeBlock code={`pnpm add -g openclaw@latest\npnpm approve-builds -g                # 批准 openclaw、node-llama-cpp、sharp 等\npnpm add -g openclaw@latest           # 重新运行以执行 postinstall 脚本`} />
      <InfoBox type="info">
        pnpm 需要显式批准带有构建脚本的包。在首次安装显示"Ignored build scripts"警告后，运行 <code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">pnpm approve-builds -g</code> 并选择列出的包，然后重新运行安装以执行 postinstall 脚本。
      </InfoBox>
      <SubTitle>然后运行新手引导</SubTitle>
      <CodeBlock code="openclaw onboard --install-daemon" />

      <SectionTitle id="man-methods">安装方式：npm vs git（安装器）</SectionTitle>
      <p className="text-slate-400 text-base mb-5">安装器支持两种方式：</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
          <div className="text-cyan-400 font-semibold mb-3 text-base">npm（默认）</div>
          <code className="text-slate-300 text-sm font-mono">npm install -g openclaw@latest</code>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
          <div className="text-cyan-400 font-semibold mb-3 text-base">git</div>
          <code className="text-slate-300 text-sm font-mono">从 GitHub 克隆/构建并从源代码 checkout 运行</code>
        </div>
      </div>
      <CodeBlock code={`# 显式 npm\ncurl -fsSL https://openclaw.ai/install.sh | bash -s -- --install-method npm\n\n# 从 GitHub 安装（源代码 checkout）\ncurl -fsSL https://openclaw.ai/install.sh | bash -s -- --install-method git`} />

      <SectionTitle id="man-flags">常用标志</SectionTitle>
      <div className="space-y-3">
        {[
          { flag: '--install-method npm|git', desc: '选择安装方式' },
          { flag: '--git-dir <path>', desc: '默认：~/openclaw' },
          { flag: '--no-git-update', desc: '使用现有 checkout 时跳过 git pull' },
          { flag: '--no-prompt', desc: '禁用提示；CI/自动化中必需' },
          { flag: '--dry-run', desc: '打印将要执行的操作；不做任何更改' },
          { flag: '--no-onboard', desc: '跳过新手引导' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 bg-slate-800/40 border border-slate-700/50 rounded-xl px-5 py-4">
            <code className="text-cyan-300 text-sm font-mono flex-shrink-0">{item.flag}</code>
            <span className="text-slate-400 text-base">{item.desc}</span>
          </div>
        ))}
      </div>

      <SectionTitle id="man-env">环境变量</SectionTitle>
      <p className="text-slate-400 text-base mb-4">等效的环境变量（对自动化有用）：</p>
      <div className="space-y-3">
        {[
          { env: 'OPENCLAW_INSTALL_METHOD=git|npm', desc: '安装方式' },
          { env: 'OPENCLAW_GIT_DIR=...', desc: 'git 目录路径' },
          { env: 'OPENCLAW_GIT_UPDATE=0|1', desc: '是否更新 git' },
          { env: 'OPENCLAW_NO_PROMPT=1', desc: '禁用提示' },
          { env: 'OPENCLAW_DRY_RUN=1', desc: '演习模式' },
          { env: 'OPENCLAW_NO_ONBOARD=1', desc: '跳过新手引导' },
          { env: 'SHARP_IGNORE_GLOBAL_LIBVIPS=0|1', desc: '默认：1；避免 sharp 针对系统 libvips 构建' },
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-4 bg-slate-800/40 border border-slate-700/50 rounded-xl px-5 py-4">
            <code className="text-emerald-300 text-sm font-mono flex-shrink-0">{item.env}</code>
            <span className="text-slate-400 text-base">{item.desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SourceContent() {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">从源代码</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          适合希望参与贡献或进行二次开发的用户。
        </p>
      </div>

      <SectionTitle id="src-steps">构建步骤</SectionTitle>
      <CodeBlock code={`git clone https://github.com/openclaw/openclaw.git\ncd openclaw\npnpm install\npnpm ui:build # 首次运行时自动安装 UI 依赖\npnpm build\nopenclaw onboard --install-daemon`} />
      <InfoBox type="tip">
        如果你还没有全局安装，请通过 <code className="text-emerald-300 bg-slate-800 px-1.5 rounded text-sm">pnpm openclaw ...</code> 运行仓库命令。
      </InfoBox>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-8">
        {[
          { step: '1', title: '克隆仓库', desc: '从 GitHub 克隆 OpenClaw 源代码到本地', icon: 'ri-git-branch-line', color: 'text-cyan-400' },
          { step: '2', title: '安装依赖', desc: '使用 pnpm 安装所有项目依赖并构建 UI', icon: 'ri-download-line', color: 'text-emerald-400' },
          { step: '3', title: '运行引导', desc: '执行 onboard 命令完成初始化配置', icon: 'ri-rocket-line', color: 'text-yellow-400' },
        ].map((item) => (
          <div key={item.step} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-6">
            <div className={`text-3xl mb-4 ${item.color}`}><i className={item.icon}></i></div>
            <div className="text-white font-semibold mb-2 text-base">步骤 {item.step}：{item.title}</div>
            <div className="text-slate-400 text-base">{item.desc}</div>
          </div>
        ))}
      </div>

      <SectionTitle id="src-tips">注意事项</SectionTitle>
      <ul className="space-y-4 text-base text-slate-300">
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>构建前请确保 Node.js 版本 ≥ 22，可通过 <code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">node -v</code> 检查。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span><code className="text-cyan-300 bg-slate-800 px-1.5 rounded text-sm">pnpm ui:build</code> 会自动安装 UI 子包的依赖，首次构建时间较长，请耐心等待。</span>
        </li>
        <li className="flex items-start gap-3">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-1 flex-shrink-0 text-lg"></i>
          <span>如需提交 PR，请先 fork 仓库，在自己的分支上开发后再提交。</span>
        </li>
      </ul>
    </div>
  );
}

function OtherContent() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const options = [
    {
      id: 'docker',
      name: 'Docker',
      icon: 'ri-server-line',
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/5 border-cyan-500/20',
      desc: '容器化部署，环境隔离，适合服务器和 CI/CD 场景。',
      code: 'docker pull openclaw/openclaw:latest\ndocker run -d -p 8080:8080 openclaw/openclaw:latest',
      anchorId: 'oth-docker',
    },
    {
      id: 'nix',
      name: 'Nix',
      icon: 'ri-snowflake-line',
      color: 'text-slate-300',
      bg: 'bg-slate-500/5 border-slate-500/20',
      desc: '使用 Nix 包管理器安装，支持声明式配置和可复现构建。',
      code: 'nix profile install github:openclaw/openclaw',
      anchorId: 'oth-nix',
    },
    {
      id: 'ansible',
      name: 'Ansible',
      icon: 'ri-robot-line',
      color: 'text-orange-400',
      bg: 'bg-orange-500/5 border-orange-500/20',
      desc: '通过 Ansible Playbook 自动化部署，适合批量服务器管理。',
      code: '# 使用官方 Ansible Role\nansible-galaxy install openclaw.openclaw',
      anchorId: 'oth-ansible',
    },
    {
      id: 'bun',
      name: 'Bun（实验性）',
      icon: 'ri-flask-line',
      color: 'text-rose-400',
      bg: 'bg-rose-500/5 border-rose-500/20',
      desc: '使用 Bun 运行时安装，仅支持 CLI 功能，目前为实验性支持。',
      code: 'bun install -g openclaw@latest',
      anchorId: 'oth-bun',
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">其他安装选项</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          除标准安装方式外，OpenClaw 还支持多种部署场景。
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {options.map((item) => (
          <div key={item.id} id={item.anchorId} className={`border rounded-2xl p-6 scroll-mt-24 ${item.bg}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`text-3xl ${item.color}`}><i className={item.icon}></i></div>
              <span className="text-white font-bold text-lg">{item.name}</span>
            </div>
            <p className="text-slate-400 text-base mb-5">{item.desc}</p>
            <div className="bg-slate-900 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 border-b border-slate-700">
                <span className="text-sm text-slate-400">shell</span>
                <button
                  onClick={() => handleCopy(item.code, item.id)}
                  className="text-slate-400 hover:text-cyan-400 cursor-pointer text-sm flex items-center gap-1.5 transition-colors"
                >
                  <i className={copiedId === item.id ? 'ri-check-line text-emerald-400' : 'ri-file-copy-line'}></i>
                  {copiedId === item.id ? '已复制' : '复制'}
                </button>
              </div>
              <pre className="p-4 text-sm text-slate-300 overflow-x-auto whitespace-pre font-mono leading-relaxed"><code>{item.code}</code></pre>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="oth-hosting">托管与部署</SectionTitle>
      <p className="text-slate-400 text-base mb-5">OpenClaw 可以部署在多种云平台和托管服务上：</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { name: 'VPS 托管', icon: 'ri-server-2-line' },
          { name: 'Fly.io', icon: 'ri-flight-takeoff-line' },
          { name: 'Hetzner', icon: 'ri-cloud-line' },
          { name: 'GCP', icon: 'ri-google-line' },
          { name: 'macOS 虚拟机', icon: 'ri-apple-line' },
          { name: 'Railway', icon: 'ri-train-line' },
          { name: 'Render', icon: 'ri-layout-grid-line' },
          { name: 'Northflank', icon: 'ri-anchor-line' },
          { name: 'exe.dev', icon: 'ri-terminal-line' },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-xl px-5 py-4 hover:border-cyan-500/30 transition-colors cursor-pointer">
            <i className={`${item.icon} text-cyan-400 text-lg`}></i>
            <span className="text-slate-300 text-base font-medium">{item.name}</span>
            <i className="ri-arrow-right-s-line text-slate-500 ml-auto"></i>
          </div>
        ))}
      </div>
    </div>
  );
}

function MaintainContent() {
  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-4">维护</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          更新、迁移和卸载 OpenClaw 的相关操作。
        </p>
      </div>

      <SectionTitle id="mnt-update">更新</SectionTitle>
      <p className="text-slate-400 text-base mb-4">使用安装器重新运行即可更新到最新版本：</p>
      <CodeBlock code="curl -fsSL https://openclaw.ai/install.sh | bash" />
      <p className="text-slate-400 text-base mb-3">或使用 npm 更新：</p>
      <CodeBlock code="npm install -g openclaw@latest" />
      <InfoBox type="tip">更新后建议运行 <code className="text-emerald-300 bg-slate-800 px-1.5 rounded text-sm">openclaw doctor</code> 检查环境是否正常。</InfoBox>

      <SectionTitle id="mnt-migrate">迁移指南</SectionTitle>
      <p className="text-slate-400 text-base mb-4">迁移到新机器时，可以导出当前配置：</p>
      <CodeBlock code={`# 导出配置\nopenclaw config export > openclaw-backup.json\n\n# 在新机器上导入\nopenclaw config import openclaw-backup.json`} />
      <InfoBox type="warning">迁移前请确保新机器已完成安装并运行过 <code className="text-amber-300 bg-slate-800 px-1.5 rounded text-sm">openclaw onboard --install-daemon</code>。</InfoBox>

      <SectionTitle id="mnt-uninstall">卸载</SectionTitle>
      <p className="text-slate-400 text-base mb-3">使用 npm 卸载：</p>
      <CodeBlock code="npm uninstall -g openclaw" />
      <p className="text-slate-400 text-base mb-3">使用 pnpm 卸载：</p>
      <CodeBlock code="pnpm remove -g openclaw" />
      <p className="text-slate-400 text-base mb-3">清理数据目录（可选）：</p>
      <CodeBlock code={`# macOS / Linux\nrm -rf ~/.openclaw\n\n# Windows\nrmdir /s %USERPROFILE%\\.openclaw`} />
      <InfoBox type="warning">删除数据目录将清除所有本地配置和会话记录，请提前备份。</InfoBox>
    </div>
  );
}
const contentMap: Record<SectionId, React.ReactNode> = {
  overview: <OverviewContent />,
  manual: <ManualContent />,
  source: <SourceContent />,
  other: <OtherContent />,
  maintain: <MaintainContent />,
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function InstallationPage() {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const saved = localStorage.getItem('installation_active_section');
    return (saved as SectionId) || 'overview';
  });
  const [activeToc, setActiveToc] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const currentToc = tocMap[activeSection];
  const currentNavItem = navItems.find(item => item.id === activeSection);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://openclaw.cc';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: 'OpenClaw 安装指南',
    description: 'OpenClaw 安装方式：npm、pnpm、源码构建、Docker 等多种安装方法详解',
    url: `${siteUrl}/installation`,
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    author: {
      '@type': 'Organization',
      name: 'OpenClaw',
    },
    publisher: {
      '@type': 'Organization',
      name: 'OpenClaw',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/lobster.svg`,
      },
    },
    inLanguage: 'zh-CN',
  };

  // 监听导航栏子菜单点击
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'installation_active_section' && e.newValue) {
        setActiveSection(e.newValue as SectionId);
      }
    };

    const handleNavChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.storageKey === 'installation_active_section') {
        setActiveSection(detail.key as SectionId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('nav-section-change', handleNavChange);

    // 页面加载时检查 localStorage
    const initialSection = localStorage.getItem('installation_active_section');
    if (initialSection && initialSection !== activeSection) {
      setActiveSection(initialSection as SectionId);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nav-section-change', handleNavChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('installation_active_section', activeSection);
    setActiveToc(currentToc[0]?.id || '');
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    const handleScroll = () => {
      const headings = currentToc
        .map((t) => document.getElementById(t.id))
        .filter(Boolean) as HTMLElement[];
      let current = headings[0]?.id || '';
      for (const el of headings) {
        if (el.getBoundingClientRect().top < 120) {
          current = el.id;
        }
      }
      setActiveToc(current);
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeSection, currentToc]);

  const handleSectionChange = (e: React.MouseEvent, id: SectionId) => {
    e.preventDefault();
    setActiveSection(id);
    setMobileMenuOpen(false);
  };

  const scrollToAnchor = (id: string) => {
    const el = document.getElementById(id);
    if (el && contentRef.current) {
      const top = el.offsetTop - 80;
      contentRef.current.scrollTo({ top, behavior: 'smooth' });
    }
    setActiveToc(id);
    setMobileTocOpen(false);
  };

  return (
    <PageTransition>
      <SEOHead
        title="OpenClaw 安装指南 | npm、Docker、源码构建"
        description="OpenClaw 安装方式：npm、pnpm、源码构建、Docker 等多种安装方法详解，支持 macOS、Linux、Windows"
        keywords="OpenClaw安装, npm安装, Docker安装, 源码构建, AI智能体安装"
        canonical={`${siteUrl}/installation`}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-slate-950 flex flex-col">
        <Navbar />

        {/* Mobile Section Selector */}
        <div className="md:hidden sticky top-16 z-40 bg-slate-900 border-b border-slate-800">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${currentNavItem?.icon} text-cyan-400`}></i>
              </div>
              <span className="text-white font-medium text-sm">{currentNavItem?.label}</span>
            </div>
            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`}></i>
          </button>

          {/* Mobile Dropdown Menu */}
          {mobileMenuOpen && (
            <div className="absolute top-full left-0 right-0 bg-slate-900 border-b border-slate-800 max-h-[70vh] overflow-y-auto">
              <nav className="p-3">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={(e) => handleSectionChange(e, item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap mb-0.5 text-left ${
                      activeSection === item.id
                        ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          )}
        </div>

        <div className="flex flex-1 pt-16" style={{ height: 'calc(100vh - 0px)' }}>
          {/* Main Content */}
          <main
            ref={contentRef}
            className="flex-1 overflow-y-auto"
            style={{ height: 'calc(100vh - 64px)' }}
          >
            <div className="max-w-6xl mx-auto px-10 md:px-20 py-14 min-h-full">
              {contentMap[activeSection]}
            </div>
            <Footer />
          </main>
        </div>

        {/* Mobile TOC Floating Button */}
        <button
          onClick={() => setMobileTocOpen(true)}
          className="md:hidden fixed bottom-6 right-6 w-12 h-12 bg-cyan-500 hover:bg-cyan-400 text-white rounded-full shadow-lg flex items-center justify-center cursor-pointer z-50 transition-colors"
        >
          <i className="ri-list-unordered text-xl"></i>
        </button>

        {/* Mobile TOC Drawer */}
        {mobileTocOpen && (
          <>
            <div
              className="md:hidden fixed inset-0 bg-black/50 z-50"
              onClick={() => setMobileTocOpen(false)}
            ></div>
            <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900 rounded-t-2xl shadow-2xl z-50 max-h-[70vh] overflow-y-auto animate-slide-up">
              <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center justify-between">
                <span className="text-white font-semibold text-sm">本页目录</span>
                <button
                  onClick={() => setMobileTocOpen(false)}
                  className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-white cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <nav className="p-4 space-y-1">
                {currentToc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToAnchor(item.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 cursor-pointer ${
                      activeToc === item.id
                        ? 'text-cyan-400 bg-cyan-500/10'
                        : 'text-slate-300 hover:bg-slate-800'
                    } ${item.level === 2 ? 'pl-6' : ''}`}
                  >
                    {activeToc === item.id && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 mr-2 mb-0.5"></span>
                    )}
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </>
        )}
      </div>
    </PageTransition>
  );
}
